import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from "@angular/core";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {Api} from "../../../../../../common/services/api.service";
import {ToastService} from "../../../../../../common/services/toast.service";
import {Modal} from "../../../../../services/modal.service";
import {HelpersService} from "../../../../../../common/services/helpers.service";
import {ConfirmComponent} from "../../../../../../common/components/confirm/confirm.component";
import {SpinnerService} from "../../../../../../common/services/spinner.service";
import {Form} from "../../../../../../common/interfaces/form.interface";
import {takeUntil} from "rxjs/operators";
import {Api3Service} from "../../../../../../common/services/api3.service";
import {Base} from "../../../../../../common/interfaces/base.interfaces";
import {Courier} from "../../../../../../common/interfaces/courier.interface";
import ISelectOption = Form.ISelectOption;

@Component({
    selector: "section-shippy-carrier-mapping-form",
    templateUrl: "form.component.html",
    styleUrls: [
        "form.component.scss"
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class ShippyCarrierMappingFormComponent implements OnInit, OnDestroy {

    private destroy$: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild(ConfirmComponent, {static: false})
    public confirmRef: ConfirmComponent;

    /**
     * Form group / group with controls
     * @type {FormGroup}
     */
    public formGroup: FormGroup = new FormGroup({
        outbound_hubs: new FormControl([]),
        destination_countries: new FormControl([]),
        outbound_hub_id: new FormControl(null, [Validators.required]),
        destination_country_iso_2: new FormControl(null, [Validators.required]),
        customer_id: new FormControl(null),
        courier_id: new FormControl(null, [Validators.required]),
        courier_service_id: new FormControl(null, [Validators.required]),
        shippy_pro_carrier_id: new FormControl(null, [Validators.required]),
        shippy_pro_carrier_name: new FormControl(null, [Validators.required]),
    });

    public selectedService: FormControl = new FormControl(null);

    public countries: ISelectOption[] = [];

    public modal: Modal.IModal;

    public action: string = "add";

    public state: Base.IState;

    public servicesList: any[] = [];

    public data: any = null;

    public couriers: ISelectOption[] = [];

    public courierServices: ISelectOption[] = [];

    public constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private toastService: ToastService,
        private helpers: HelpersService,
        private spinnerService: SpinnerService,
        private api3Service: Api3Service
    ) {
    }

    private async prepareCountriesList(): Promise<any> {
        this.spinnerService.show();
        this.countries = await this.helpers.prepareCountriesList();
        this.spinnerService.hide();
        this.changeDetectorRef.markForCheck();
    }


    /**
     * Prepare form group (create)
     * @returns {void}
     */
    private prepareForm(): void {

        if (!this.data.id) {
            return;
        }

        this.formGroup.patchValue(this.data);

        this.getCouriersServices(this.data.courier_id);

        this.setServiceNameValue(this.data.shippy_pro_carrier_id);

        this.changeDetectorRef.detectChanges();
    }

    private async getCarrierServices(hubs: number[] = []): Promise<any> {

        const {data}: Api.IResponse = await this.api3Service.get(
            `${this.state.section}/shipping/carriers-services-list`, {
                hubs
            });

        if (data) {
            this.servicesList = data;
            this.changeDetectorRef.markForCheck();
        }
    }

    private async getCouriers(): Promise<any> {

        const {data}: Api.IResponse = await this.api3Service.get(
            `${this.state.section}/couriers`, {
                data_structure: "select",
            });

        if (data) {
            this.couriers = data;
            this.changeDetectorRef.markForCheck();
        }
    }

    private async getCouriersServices(courier_id: number): Promise<any> {
        this.spinnerService.show();
        const {data}: Api.IResponse = await this.api3Service.get(
            `${this.state.section}/couriers/${courier_id}/services`, {
                data_structure: "select"
            });

        this.spinnerService.hide();

        if (data) {
            this.courierServices = data;
            this.changeDetectorRef.markForCheck();
        }
    }

    private setServiceNameValue(value: number): void {
        this.formGroup.get("shippy_pro_carrier_name").setValue(this.servicesList.find((service: any) => {
            return service.carrier_id === value;
        })?.name);
    }

    private async init(): Promise<any> {
        await this.getCarrierServices();
        await this.prepareCountriesList();
        await this.getCouriers();

        this.prepareForm();

        this.formGroup.get("shippy_pro_carrier_id").valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe((value: number) => {
                if (!value) {
                    return;
                }
                this.setServiceNameValue(value);
            });


        this.formGroup.get("courier_id").valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe((value: number) => {
                if (!value) {
                    return;
                }

                this.getCouriersServices(value);
            });

        this.formGroup.get("outbound_hubs").valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe((hubs: number[]) => {
                if (!hubs.length) {
                    return;
                }

                this.getCarrierServices(hubs);
            });
    }

    public onHubSelected($event: any): void {
        if (Array.isArray($event)) {
            this.formGroup.get("outbound_hubs").setValue($event);
            this.formGroup.get("outbound_hub_id").setValue($event[0]);
        } else {
            this.formGroup.get("outbound_hub_id").setValue($event);
        }
    }

    public onCountrySelected($event: any): void {
        if (Array.isArray($event)) {
            this.formGroup.get("destination_countries").setValue($event);
            this.formGroup.get("destination_country_iso_2").setValue($event[0]);
        } else {
            this.formGroup.get("destination_country_iso_2").setValue($event);
        }
    }

    public async handleFormSubmit(): Promise<any> {
        this.spinnerService.show();

        const {code, message}: Api.IResponse = await this.api3Service[this.action === "add" ? "post" : "put"](
            `${this.state.section}/shipping/carriers-services-map`
            + (this.action === "edit" ? "/" + this.modal.params.data.id : ""),
            this.formGroup.value
        );

        this.spinnerService.hide();

        if (code === 200) {
            this.toastService.show(message, "success");
            this.modal.response.emit({name: "OK"});
        }
    }


    public async delete(): Promise<any> {
        if (!await this.confirmRef.confirm("Are you sure want to delete this mapping?")) {
            return;
        }

        this.spinnerService.show();

        const {message, type}: Api.IResponse = await this.api3Service.delete(
            `${this.state.section}/shipping/carriers-services-map/${this.modal.params.data.id}`);

        this.spinnerService.hide();

        if (type as string === "success") {
            this.toastService.show(message, "success");
            this.showList();
        }
    }


    public showList(): void {
        this.modal.response.emit();
    }


    public ngOnInit(): void {
        this.action = this.modal.params.action ?? "add";
        this.state = this.modal.params.state;
        this.data = this.modal.params.data ? this.modal.params.data : {};

        this.init();
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }
}
